import { useContext, useState } from "react";
import { FormattedMessage } from "react-intl";

import { Dataset, ProjectRead } from "@nens/rana/api-v1";
import { RanaButton, RanaModal } from "@nens/ranalib";

import { TenantProjectListTable, useTenantProjectListData } from "@/features/project";
import { GoToExtractDatasetProcessFunc } from "@/lib/catalogue";
import { ProjectContext } from "@/stores/project-provider";

import { ExtractProcessRunInModal } from "./dataset-detail-extract-process-run";

export interface DatasetDetailExtractProps {
  dataset: Dataset;
  goToExtractDatasetProcess: GoToExtractDatasetProcessFunc;
}

export type DatasetDetailExtractOpenModal =
  | {
      type: "project";
    }
  | { type: "process"; project: ProjectRead }
  | null;

export const DatasetDetailExtract = ({
  dataset,
  goToExtractDatasetProcess,
}: DatasetDetailExtractProps) => {
  const projectFromContext = useContext(ProjectContext);
  const [openModal, setOpenModal] = useState<DatasetDetailExtractOpenModal>(null);
  const onClose = () => setOpenModal(null);
  if (projectFromContext.id)
    return (
      <HasProjectFromContextButton
        dataset={dataset}
        goToExtractDatasetProcess={goToExtractDatasetProcess}
      />
    );

  return (
    <>
      {openModal && (
        <RanaModal
          topBar={
            openModal?.type === "project" ? (
              <FormattedMessage id="heading.select_project" defaultMessage="Select project" />
            ) : (
              <FormattedMessage id="heading.select_process" defaultMessage="Select process" />
            )
          }
          w="full"
          h="full"
          isOpen
          onClose={onClose}
        >
          {openModal?.type === "project" && <ProjectListInModal setOpenModal={setOpenModal} />}
          {openModal?.type === "process" && (
            <ExtractProcessRunInModal
              setOpenModal={setOpenModal}
              dataset={dataset}
              goToExtractDatasetProcess={goToExtractDatasetProcess}
              project={openModal.project}
            />
          )}
        </RanaModal>
      )}
      <RanaButton
        variant="primary"
        onClick={() => {
          setOpenModal({ type: "project" });
        }}
      >
        <FormattedMessage id="button.take_extract" defaultMessage="Take extract" />
      </RanaButton>
    </>
  );
};

type HasProjectFromContextButtonProps = DatasetDetailExtractProps;
const HasProjectFromContextButton = ({
  goToExtractDatasetProcess,
  dataset,
}: HasProjectFromContextButtonProps) => {
  const project = useContext(ProjectContext);
  return (
    <RanaButton
      variant="primary"
      onClick={() =>
        goToExtractDatasetProcess({ projectSlug: project.slug + "", datasetId: dataset.id })
      }
    >
      <FormattedMessage id="button.choose_dataset" defaultMessage="Choose dataset" />
    </RanaButton>
  );
};

interface ProjectListInModalProps {
  setOpenModal: (openModal: DatasetDetailExtractOpenModal) => void;
}

const ProjectListInModal = ({ setOpenModal }: ProjectListInModalProps) => {
  const projectListHookReturn = useTenantProjectListData();
  return (
    <TenantProjectListTable
      {...projectListHookReturn}
      getRowProps={(project) => ({
        onClick: () => setOpenModal({ type: "process", project }),
      })}
    />
  );
};
